import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useMoneyFormatter } from "@/lib/currency";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [company, setCompany] = useState(null);
  const [config, setConfig] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const loc = useLocation();
  const nav = useNavigate();

  const load = async () => {
    const [p, c, b] = await Promise.all([
      api.get("/plans"),
      api.get("/companies/me").catch(() => ({ data: null })),
      api.get("/public/billing-config"),
    ]);
    setPlans(p.data); setCompany(c.data); setConfig(b.data);
  };
  useEffect(() => { load(); }, []);

  // After Stripe redirect: poll status
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (cancelled) return;
        if (data.status === "PAID") { toast.success("Subscription activated"); load(); nav("/billing", { replace: true }); return; }
        if (data.status === "EXPIRED") { toast.error("Checkout expired"); nav("/billing", { replace: true }); return; }
      } catch (err) {
        // Transient poll failure — retry until attempts exhausted
        console.warn("billing status poll failed", err);
      }
      if (cancelled) return;
      if (attempts < 8) setTimeout(poll, 2500);
      else nav("/billing", { replace: true });
    };
    poll();
    return () => { cancelled = true; };
  }, [loc.search]);

  const subscribe = async (plan_id) => {
    setBusy(plan_id);
    try {
      const { data } = await api.post("/billing/checkout-session", {
        plan_id, billing_cycle: cycle, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  const fmt = useMoneyFormatter();

  return (
    <div data-testid="billing-page">
      <PageHeader title="Billing" subtitle="Subscription · Stripe checkout" />

      {company && (
        <Section title="Current subscription" testId="current-subscription">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <Pill variant={company.subscription_status === "ACTIVE" ? "success" : company.subscription_status === "TRIAL" ? "info" : "warning"}>{company.subscription_status}</Pill>
            <span className="text-muted-foreground">Plan: {company.subscription_plan_id ? company.subscription_plan_id.slice(0, 8) + "…" : "—"}</span>
            {company.current_period_end && <span className="text-muted-foreground">Renews: {new Date(company.current_period_end).toLocaleDateString()}</span>}
          </div>
        </Section>
      )}

      {!config?.configured && (
        <Section title="Stripe not configured" testId="stripe-warning">
          <div className="text-sm text-secondary">
            The platform&apos;s Stripe keys are not yet configured by the Global Admin. Subscription checkout will be unavailable until they are added in Platform Settings.
          </div>
        </Section>
      )}

      <div className="flex items-center justify-end gap-3 mb-4">
        <span className="label">Billing cycle</span>
        <div className="inline-flex rounded-md border border-border bg-white overflow-hidden text-sm">
          <button className={`px-4 py-2 ${cycle === "monthly" ? "bg-forest text-white" : "hover:bg-muted"}`} onClick={() => setCycle("monthly")} data-testid="cycle-monthly">Monthly</button>
          <button className={`px-4 py-2 ${cycle === "annual" ? "bg-forest text-white" : "hover:bg-muted"}`} onClick={() => setCycle("annual")} data-testid="cycle-annual">Annual</button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p) => (
          <div key={p.id} className="card-flat p-6 flex flex-col" data-testid={`billing-plan-${p.id}`}>
            <div className="label mb-2">{p.name}</div>
            <div className="metric mb-1">{fmt(cycle === "monthly" ? p.monthly_price : p.annual_price)}</div>
            <div className="text-xs text-muted-foreground mb-4">per {cycle === "monthly" ? "month" : "year"}</div>
            <p className="text-sm text-muted-foreground mb-4">{p.description}</p>
            <ul className="text-sm space-y-2 mb-6 flex-1">
              {p.features?.map((f, i) => (
                <li key={i} className="flex gap-2 items-start"><Check className="w-4 h-4 mt-0.5 text-forest" /><span>{f}</span></li>
              ))}
            </ul>
            <button onClick={() => subscribe(p.id)} disabled={busy === p.id} className="btn-primary w-full" data-testid={`subscribe-${p.id}`}>
              {busy === p.id ? "Redirecting…" : "Subscribe"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
